import type { PriceConfig } from '@/lib/api/categories';

// Currency types matching the server
export type CurrencyType = 'comoros_currency' | 'european_currency' | 'american_currency';

// Currency symbol map for all possible formats from server
const CURRENCY_SYMBOL_MAP: Record<string, string> = {
  comoros_currency: 'KMF',
  european_currency: '€',
  american_currency: '$',
  euro: '€',
  EUR: '€',
  USD: '$',
  KMF: 'KMF',
};

// Currency labels for display
export const CURRENCY_LABELS: Record<CurrencyType, string> = {
  comoros_currency: 'Franc Comorien',
  european_currency: 'Euro',
  american_currency: 'Dollar US',
};

/**
 * Get the currency symbol for a given currency type.
 * Handles multiple formats: 'european_currency', 'EUR', 'euro', etc.
 */
export function getCurrencySymbol(currency: string): string {
  return CURRENCY_SYMBOL_MAP[currency] || currency;
}

/**
 * Get the currency label for a given currency type
 */
export function getCurrencyLabel(currency: CurrencyType): string {
  return CURRENCY_LABELS[currency] || 'Franc Comorien';
}

/**
 * Convert price using configPrice exchange rates
 * Exchange rates in configPrice represent: 1 EUR = X units of currency
 *
 * @param fromPrice - The price to convert
 * @param fromCurrency - Source currency
 * @param toCurrency - Target currency
 * @param configPrice - Exchange rate configuration array
 * @returns Converted price
 */
export function convertPrice(
  fromPrice: number,
  fromCurrency: CurrencyType,
  toCurrency: CurrencyType,
  configPrice: PriceConfig[]
): number {
  if (fromCurrency === toCurrency) return fromPrice;
  if (!configPrice.length) return 0;
  if (fromPrice <= 0) return 0;

  // Find exchange rates in configPrice
  const fromConfig = configPrice.find(config => config.name === fromCurrency);
  const toConfig = configPrice.find(config => config.name === toCurrency);

  if (!fromConfig || !toConfig) return 0;

  let convertedPrice: number;

  if (fromCurrency === 'european_currency') {
    // Converting FROM EUR to another currency
    convertedPrice = fromPrice * toConfig.price;
  } else if (toCurrency === 'european_currency') {
    // Converting TO EUR from another currency
    convertedPrice = fromPrice / fromConfig.price;
  } else {
    // Converting between two non-EUR currencies (via EUR)
    // First convert to EUR, then to target currency
    const priceInEur = fromPrice / fromConfig.price;
    convertedPrice = priceInEur * toConfig.price;
  }

  return convertedPrice;
}

/**
 * Format a price with its currency symbol
 */
export function formatPriceWithSymbol(
  price: number,
  currency: CurrencyType,
  decimals = 2
): string {
  const symbol = getCurrencySymbol(currency);
  const formattedPrice = price.toFixed(decimals);

  // For KMF, no decimals and symbol after
  if (currency === 'comoros_currency') {
    return `${Math.round(price).toLocaleString('fr-FR')} ${symbol}`;
  }

  // For EUR and USD, symbol position varies
  if (currency === 'american_currency') {
    return `${symbol}${parseFloat(formattedPrice).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  }

  return `${parseFloat(formattedPrice).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} ${symbol}`;
}

/**
 * Get all currency conversions for a given price
 */
export function getAllConversions(
  price: number,
  sourceCurrency: CurrencyType,
  configPrice: PriceConfig[]
): Array<{ currency: CurrencyType; value: number; symbol: string; label: string }> {
  const currencies: CurrencyType[] = ['comoros_currency', 'european_currency', 'american_currency'];

  return currencies
    .filter(currency => currency !== sourceCurrency)
    .map(currency => ({
      currency,
      value: convertPrice(price, sourceCurrency, currency, configPrice),
      symbol: getCurrencySymbol(currency),
      label: getCurrencyLabel(currency),
    }));
}
